import { makeStyles, withStyles } from '@material-ui/core';
import { Linked } from '@type-r/react';
import * as React from 'react';
import { ComponentProps, JSXElementConstructor, ReactNode, useContext } from 'react';
import { useQaId } from './qa-ids';
import { BaseCheckbox } from './checkbox';
import { BaseInput, InputProps, inputStyles, ReadOnly } from './inputs';
import { BaseSelect } from './select';
import { BaseTabProps, BaseTabs } from './tabs';

type LinkedFieldProps<ValueType, C extends keyof JSX.IntrinsicElements | JSXElementConstructor<any>> = ComponentProps<C> & {
    label : string
    $value? : Linked<ValueType>
}

export const CompactTextArea =
    ({ label, className, $value, value, readOnly, ...props } : LinkedFieldProps<string, 'textarea'> ) => {
        const ro = useContext( ReadOnly ) || readOnly || !$value,
            classes = useCompactInputStyles( props ),
            maybeError = !ro && !props.disabled && $value.error ? ' error' : '';

        return (
            <label className={classes.root + ' ' + classes.column + maybeError}>
                <span className="label">{label}</span>
                { $value && !ro ?
                    <textarea className={ classes.input + maybeError } {...props} {...$value.props} /> :
                    <textarea readOnly className={ classes.input } value={ $value.value? $value.value : value } {...props} />
                }
            </label>
        )
    }

export const CompactInput = ({ label, tooltip, ...props } :
    InputProps & (
        { label : string, tooltip? : any, noMargins? : boolean, 'qa-id'? : string } | 
        { label : ReactNode, tooltip? : any, noMargins? : boolean, 'qa-id'? : string }
    )
) => {
        const ro = useContext( ReadOnly ) || props.readOnly || !props.$value || props.disabled,
            classes = useCompactInputStyles( props ),
            maybeError = !ro && props.$value.error ? ' error' : '';

            const nextId = useQaId( props.id || label );
        return (
            <label className={classes.root + ' ' + classes.row + maybeError}>
                <span className="label filled">{label}</span>
                <BaseInput classes={ classes } { ...props } data-qa-id={ nextId }/>
                { tooltip &&
                    <span className="lqx-tooltip">
                        { tooltip }
                    </span>
                }
            </label>
        )
    }

const useCompactInputStyles = makeStyles({
    root : {
        '& .lqx-tooltip' : {
            display: 'none',
            zIndex: 2,
            position: 'absolute',
            top: '110%',
            backgroundColor: '#404040',
            color: 'white',
            padding: 8,
            borderRadius: 3,
            maxWidth: 550,
            fontSize: 12,
            boxShadow: '2px 0 4px 0 rgba(0,0,0,0.1)',
        },

        '&:hover .lqx-tooltip' : {
            display : 'block',
        },

        display: 'flex',
        border: '1px solid #CEDCE3',
        borderRadius: 2,
        position: 'relative',

        marginTop: ( p : any ) => p.noMargins? 0 : 12,
        marginBottom: ( p : any ) => p.noMargins? 0 : 12,

        backgroundColor: ( props : any ) => props.disabled ? '#F9F9F9' : 'white',

        '&:focus-within' : {
            border: '1px solid #1084D3',

            '& span.label' : {
                color: '#1084D3',
                '&.filled' : {
                    borderRight: '1px solid #1084D3',
                }
            }
        },

        '& span.label' : {
            color: '#899095',
            fontSize: 12,
            fontWeight: 600,
            padding: '5px 0px 4px 7px',
            textTransform: 'uppercase'
        },

        '& span.filled' : {
            backgroundColor: '#F9F9F9',// !
            borderRight: '1px solid #CEDCE3',//!
            minWidth: '16em'
        },

        '&:hover' : ( props : any ) => (
            props.disabled ? {} : {
                border: '1px solid #1084D3'
            }
        ) as any,
        '&.error' : {
            border: '1px solid #FF274B !important',

            '& span.label' : {
                color: '#FF274B !important',

                '&.filled' : {
                    borderRight: '1px solid #FF274B !important',
                }
            }
        }
    },

    tooltip : {

    },

    tabs : {
        display: 'flex',
        justifyContent: ( props : any ) => props.label ? 'flex-end' : 'center',
        padding: 7,
        color: '#899095',
        marginTop: 4,
        marginBottom: 4,
        alignItems: 'center',

        '& .label': {
            fontSize: 12,
            fontWeight: 600,
            textTransform: 'uppercase',
            justifySelf: 'start',
            flexGrow: 2,
            verticalAlign: 'center'
        },

        '& .tab': {
            paddingLeft: '1em',
            paddingRight: '1em',
            fontSize: 13,
            cursor: 'pointer',

            '&.selected': {
                color: '#1084D3',
                borderBottom: '#1084D3 2px solid',
            }
        }
    },

    number : {
        textAlign : 'end'
    },

    column : {
        flexDirection: "column", //!
    },
    error: {},

    row : {
        flexDirection: 'row', //!
    },

    input : {
        ...inputStyles,
        flexGrow: 1,
        borderWidth : 0,
        color: 'black',
        fontSize: 12,
        padding: '5px 7px 4px 7px',
        fontFamily: 'Montserrat',
        fontWeight: 600,
        resize : 'none',

        '&:focus' : {
            outline: 'none',
        },

        '&:disabled' : {
            opacity: 0.5,
        }
    }
})

export const CompactSelect = withStyles({
    root : {
        borderWidth: 1,
        borderStyle: 'solid',
        borderRadius: 2,

        display: 'flex',
        border: '1px solid #CEDCE3',
        position: 'relative',

        fontSize: 12,
        fontWeight: 600,

        '& .label' : {
            minWidth: '16em'
        },

        '&:focus-within' : {
            border: '1px solid #1084D3',

            '& .label' : {
                borderRight: '1px solid #1084D3',
            }
        },

        '&.error' : {
            border: '1px solid #FF274B !important',

            '& .label' : {
                borderRight: '1px solid #FF274B !important',
            }
        },
    },

    label : {
        padding: '5px 7px 4px 7px',
        color: '#899095',
        textTransform: 'uppercase',

        backgroundColor: '#F9F9F9',
        borderRight: '1px solid #CEDCE3',
    },

    selectInputRoot : {
    },

    selectInput : {
        flexGrow: 1,
        borderWidth : 0,
        fontSize: 12,
        fontFamily: 'Montserrat',
        fontWeight: 600,
        textTransform: 'uppercase',

        '&:focus' : {
            outline: 'none',
        },

        '&:disabled' : {
            opacity: 0.5,
        }
    },
})(BaseSelect)

export const CompactCheckbox = withStyles({
    label: {
        textTransform: 'uppercase',
    },
})(BaseCheckbox);

export type CompactTabProps = Omit<BaseTabProps, 'classes'>;

export const CompactTabs = (props : CompactTabProps ) => {
    const classes = useCompactInputStyles( props );

    return (
        <BaseTabs classes={ classes } { ...props }></BaseTabs>
    );
};

export const FullWidthTabs = withStyles({
    tabs : {
        justifyContent: 'space-between',
        display: 'flex',
        alignItems: 'center',
        margin: 0,

        '& .tab' : {
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            cursor: 'pointer',
            margin: "0 1px 0 1px",
            width: '100%',
            padding: 4,
            backgroundColor: '#6681a217',

            '&.selected' : {
                backgroundColor: 'white',
                fontWeight: 600,
                color : "#1d83ff"
            },

            '& .title' : {
                fontWeight: 500,
                fontSize: 14
            },

            '& .body' : {
                fontSize: 12
            } 
        }
    }

})( BaseTabs );
